"use client";

import React from "react";
import { MessageSquare, ExternalLink } from "lucide-react";
import { useRouter } from "next/navigation";
import { formatDistanceToNow } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useCommunicationsForContact } from "@/lib/hooks/api/communications";
import type { TabContentProps } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { Communications } from "@/src/gql/graphql";

/**
 * Communications Related Activities Display Tab Component
 */
export function CommunicationsRelatedActivitiesDisplay({ item }: TabContentProps<Communications>) {
  const router = useRouter();
  const communicationWithExtended = item as unknown as any;
  
  // Fetch related communications for the same contact
  const { data: relatedCommunications = [], isLoading, error } = useCommunicationsForContact(
    communicationWithExtended.contact_id
  );

  // Filter out the current communication and sort by date
  const filteredCommunications = relatedCommunications
    .filter((comm) => comm.id !== item.id)
    .sort((a, b) => {
      const dateA = new Date(a.occurred_at || a.created_at || 0).getTime();
      const dateB = new Date(b.occurred_at || b.created_at || 0).getTime();
      return dateB - dateA; // Most recent first
    })
    .slice(0, 10); // Show top 10 related communications

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading related communications...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <div className="text-center py-8 text-red-600 dark:text-red-400">
            <p>Failed to load related communications</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (filteredCommunications.length === 0) {
    return (
      <Card>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No other communications with this contact</p>
            <p className="text-sm mt-2">This is the only communication on record</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base md:text-lg font-semibold text-card-foreground mb-2">
          Related Communications ({filteredCommunications.length})
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Other communications with {communicationWithExtended.contact_name || "this contact"}
        </p>
      </div>

      <div className="space-y-2">
        {filteredCommunications.map((comm) => (
          <div
            key={comm.id}
            onClick={() => router.push(`/communications/${comm.id}`)}
            className="p-3 md:p-4 border border-gray-200 rounded-lg hover:bg-blue-50 cursor-pointer transition-colors dark:border-gray-700 dark:hover:bg-blue-950/40"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {comm.title || "Untitled Communication"}
                  </span>
                  <Badge variant="outline" className="text-xs flex-shrink-0">
                    {comm.direction || "Unknown"}
                  </Badge>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                  {comm.body_preview || comm.body || "No description"}
                </p>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                  <span>{comm.type_label || comm.type_id || "Unknown Type"}</span>
                  <span>
                    {comm.occurred_at
                      ? formatDistanceToNow(new Date(comm.occurred_at), { addSuffix: true })
                      : formatDistanceToNow(new Date(comm.created_at || 0), { addSuffix: true })}
                  </span>
                  <Badge
                    variant="secondary"
                    className={`text-xs ${
                      comm.status === "completed"
                        ? "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200"
                        : comm.status === "pending"
                          ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200"
                          : "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
                    }`}
                  >
                    {comm.status || "Unknown"}
                  </Badge>
                </div>
              </div>
              <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />
            </div>
          </div>
        ))}
      </div>

      {filteredCommunications.length >= 10 && (
        <div className="text-center pt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => router.push(`/communications?contact_id=${communicationWithExtended.contact_id}`)}
          >
            View All Communications
          </Button>
        </div>
      )}
    </div>
  );
}
